import classNames from "classnames";
import isEmpty from "lodash/fp/isEmpty";
import { useState } from "react";

import Button from "@rio-cloud/rio-uikit/Button";
import ButtonDropdown from "@rio-cloud/rio-uikit/ButtonDropdown";
import NotFoundState from "@rio-cloud/rio-uikit/NotFoundState";
import SortArrows from "@rio-cloud/rio-uikit/SortArrows";
import SortDirection, { type SortDirectionType } from "@rio-cloud/rio-uikit/SortDirection";
import TableCardsSorting from "@rio-cloud/rio-uikit/TableCardsSorting";
import TableSearch from "@rio-cloud/rio-uikit/TableSearch";
import TableToolbar from "@rio-cloud/rio-uikit/TableToolbar";
import TableViewToggles, { type TableViewTogglesViewType } from "@rio-cloud/rio-uikit/TableViewToggles";
import { formatCategory, formatMinutesToBrHour, formatStayTime } from "@utils";
import { CheckpointV2 } from "../../types/checkpoint";
import { isFavoriteCheckpoint } from "../Checkpoint";
import Paginator from "../Paginator";
import { IStop } from "../RegisterRoute";

const defaultColumnOrder = ["name", "tipo", "stages", "totalPlannedTimeMinutes", "actions"];

type ColumnLabel = {
  [key: string]: string;
};
const columnLabels: ColumnLabel = {
  name: "Checklist",
  tipo: "Tipo do checklist",
  stages: "Etapas",
  totalPlannedTimeMinutes: "Tempo Total",
  actions: "actions",
};

type ColumnDetails = {
  [key: string]: number;
};

type ColumnDetailsMap = {
  [key: string]: ColumnDetails;
};

const demoColumnsDetails: ColumnDetailsMap = {
  name: {
    width: 260,
    defaultWidth: 260,
    maxWidth: 260,
  },
};

const getSortDir = (sortDir: SortDirectionType, sortBy: string, previousSortBy: string) => {
  if (sortBy === previousSortBy) {
    return sortDir === SortDirection.ASCENDING ? SortDirection.DESCENDING : SortDirection.ASCENDING;
  }
  return SortDirection.ASCENDING;
};

export type Props = {
  viewType: TableViewTogglesViewType;
  pages: number;
  searchValue: string;
  page: number;
  pageSize: number;
  usersAmity?: Amity.User[];
  handleSearchValueChange: (text: string) => void;
  handlePage: (params: { page: number, pages?: number }) => void;
  handleSort: (dir: string, newSort: string) => void;
  handleFilter?: () => void;
  onDateFilterChange?: (start?: string, end?: string) => void;
  handleCreateCheckpoint: () => void;
  handleDelete: (id?: string) => void;
  handleUpdateCheckpoint: (id?: string) => void;
  handleRemove?: (stop?: IStop) => void;
  handleSelect?: (stop?: IStop) => void;
  rows: CheckpointV2[];
  selected?: IStop;
  handleFavorite?: (checkpoint: isFavoriteCheckpoint) => void;
};

const TableCheckpoints = (props: Props) => {
  const { viewType: externalViewType } = props;
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState<SortDirectionType>(SortDirection.ASCENDING);
  const [columnOrder, setColumnOrder] = useState<string[]>(defaultColumnOrder);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [columnsDetails, setColumnsDetails] = useState(demoColumnsDetails);
  const [viewType, setViewType] = useState(externalViewType || TableViewToggles.VIEW_TYPE_TABLE);

  const handleSortChange = (event: React.MouseEvent<HTMLElement>) => {
    const newSortBy = event.currentTarget.getAttribute("data-sortby");
    if (newSortBy) {
      handleCardSortChange(newSortBy, getSortDir(sortDir, newSortBy, sortBy));
      props.handleSort(sortDir, newSortBy);
    }
  };

  const handleCardSortChange = (newSortBy: string, newSortDir: SortDirectionType) => {
    setSortBy(newSortBy);
    setSortDir(newSortDir);
  };

  // May be extracted as a dedicated component but for demo purpose it's shown here
  const renderTableHead = (column: string, label: string, sortByColumn: string, sortDirection: SortDirectionType) => {
    const tableHeadClassNames = classNames("user-select-none", "sort-column");

    return (
      <th
        key={column}
        className={tableHeadClassNames}
        onClick={handleSortChange}
        data-field={column}
        data-sortby={column}
        title={label}
      >
        {column !== "actions" ? (
          <span>
            {sortByColumn === column ? <SortArrows direction={sortDirection} /> : <SortArrows />}
            <span>{label}</span>
          </span>
        ) : null}
      </th>
    );
  };

  const renderTableCaption = (column: string, columnDetails: ColumnDetails) => {
    const style = columnDetails?.width
      ? {
        minWidth: columnDetails.width,
        width: columnDetails.width,
      }
      : {};

    return <col key={column} style={style} />;
  };

  // filter for hidden columns
  const columns = columnOrder.filter((name) => !hiddenColumns.includes(name));

  const tableClassNames = classNames("table", "table-bordered", "table-head-filled");

  const isViewTypeTable = viewType === TableViewToggles.VIEW_TYPE_TABLE;

  const cardSortingSelectOptions = columns.map((column) => {
    return {
      id: column,
      label: columnLabels[column],
      selected: column === sortBy,
      disabled: false,
    };
  });

  return (
    <div id="table-checkpoints">
      <div style={{ marginBottom: 24 }}>
        <TableToolbar>
          <div className="table-toolbar-container">
            <div className="table-toolbar-group-left">
              <div className="table-toolbar-column margin-bottom-0">
                <div className="btn-toolbar table-btn-toolbar">
                  <Button
                    bsStyle={Button.PRIMARY}
                    iconName="rioglyph-plus"
                    onClick={() => props.handleCreateCheckpoint()}
                  >
                    Criar Checklist
                  </Button>
                  <div>
                    <TableSearch
                      value={props.searchValue}
                      onChange={props.handleSearchValueChange}
                      placeholder="Buscar em checklist de parada"
                    />
                  </div>
                  <div className="gap-10 display-flex " style={{ marginTop: -20 }}></div>
                </div>
              </div>
            </div>
          </div>
        </TableToolbar>
      </div>
      {sortBy && !isViewTypeTable && (
        <TableCardsSorting
          selectOptions={cardSortingSelectOptions}
          sortName={sortBy}
          sortOrder={sortDir}
          onSortChange={handleCardSortChange}
        />
      )}
      <div>
        {isEmpty(props.rows) && !isViewTypeTable && (
          <NotFoundState headline="Sem resultados" message="Por favor refaça a busca." />
        )}
        <table className={tableClassNames}>
          <colgroup>
            {columns.map((column) => renderTableCaption(column, columnsDetails[column]))}
            <col className="table-action" />
          </colgroup>
          <thead>
            <tr>
              {columns.map((column) => renderTableHead(column, columnLabels[column], sortBy, sortDir))}
              <th className="table-action" />
            </tr>
          </thead>
          <tbody>
            {isEmpty(props.rows) && isViewTypeTable && (
              <tr>
                <td colSpan={columns.length + 1}>
                  <NotFoundState
                    outerClassName="border-none"
                    headline="Sem resultados"
                    message="Por favor refaça a busca."
                  />
                </td>
              </tr>
            )}

            {props.rows.map((row: CheckpointV2, index: number) => {
              return (
                <tr key={index}>
                  <td key={"Checkpoint"} data-field={"Checkpoint"}>
                    <span className="rioglyph rioglyph-detail-view-info text-size-16 text-color-gray"></span>
                    <span className="margin-left-10 ">{row.title}</span>
                  </td>

                  <td key={"tipo"} data-field={"tipo"}>
                    <span className="rioglyph rioglyph-detail-view-info text-size-16 text-color-gray"></span>
                    <span className="margin-left-10">{formatCategory(row?.checkpointType)}</span>
                  </td>

                  <td key={"Etapas"} data-field={"Etapas"}>
                    <span className="rioglyph rioglyph-checkboxes text-size-16 text-color-gray"></span>
                    <span className="margin-left-10">
                      {row.steps?.length} {row.steps?.length && row.steps?.length < 2 ? "etapa" : "etapas"}
                    </span>
                  </td>

                  <td key={"Tempo Total"} data-field={"Tempo Total"}>
                    <span className="rioglyph rioglyph-time text-size-16 text-color-gray"></span>
                    <span className="margin-left-10">{formatMinutesToBrHour(row.estimatedTimeMinutes)}</span>
                  </td>

                  <td key={"actions"} data-field={"actions"} colSpan={2}>
                    <div className="display-flex gap-15 width-100pct justify-content-end align-items-center">
                      <div>
                        {row.isFavorite ? (
                          <span
                            onClick={() => {
                              if (props.handleFavorite) {
                                props?.handleFavorite({
                                  isFavorite: false,
                                  id: row.id,
                                });
                              }
                            }}
                            className="rioglyph rioglyph-star text-color-primary text-size-h4 cursor-pointer hover-text-color-highlight-light"
                          />
                        ) : (
                          <span
                            onClick={() => {
                              if (props.handleFavorite) {
                                props?.handleFavorite({
                                  isFavorite: true,
                                  id: row.id,
                                });
                              }
                            }}
                            className="rioglyph rioglyph-star text-color-primary text-size-h4 cursor-pointer text-color-highlight-light hover-text-color-primary"
                          />
                        )}
                      </div>
                      {props.selected?.checkpointId === row.id ? (
                        <Button
                          onClick={() => {
                            if (props.handleRemove) {
                              props?.handleRemove({
                                ...props.selected,
                                stayTime: "",
                                checkpointId: null,
                                weight: 0,
                              } as IStop);
                            }
                          }}
                          iconName="rioglyph rioglyph-remove"
                          className="rounded-small width-150"
                          bsStyle={Button.PRIMARY}
                        >
                          Remover
                        </Button>
                      ) : (
                        <Button
                          onClick={() => {
                            if (props.handleSelect) {
                              props?.handleSelect({
                                ...props.selected,
                                category: row.checkpointType,
                                stayTime: formatStayTime(row?.estimatedTimeMinutes),
                                checkpointId: row.id,
                                weight: row.quantity,
                              } as IStop);
                            }
                          }}
                          className="rounded-small width-150"
                        >
                          Selecionar
                        </Button>
                      )}

                      <span>
                        <ButtonDropdown
                          bsSize="md"
                          dropdownClassName="width-200"
                          title={<span className="rioglyph rioglyph-option-vertical" />}
                          variant="link"
                          iconOnly
                          items={[
                            {
                              value: (
                                <>
                                  <span className="rioglyph rioglyph-pencil margin-right-10 text-color-primary" />
                                  <span>Editar</span>
                                </>
                              ),
                              onSelect: () => props?.handleUpdateCheckpoint(row?.id),
                            },
                            {
                              value: (
                                <>
                                  <span className="rioglyph rioglyph-trash margin-right-10 text-color-primary" />
                                  <span>Deletar</span>
                                </>
                              ),
                              onSelect: () => props?.handleDelete(row?.id),
                            },
                          ]}
                        />
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Paginator
        handlePage={params => props.handlePage(params)}
        page={props.page}
        pages={props.pages}
        pageSize={props.pageSize}
        disabled={props.rows.length == 0}
      />
    </div>
  );
};

export default TableCheckpoints;
